import type { ComponentType, ReactNode, SVGProps } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  canConfirm?: boolean;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  submittingLabel: string;
  helperText?: ReactNode;
  children: ReactNode;
};

export const TrialConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  submitting,
  canConfirm = true,
  icon: Icon,
  title,
  description,
  confirmLabel,
  submittingLabel,
  helperText,
  children,
}: Props) => (
  <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-lime-700" strokeWidth={2} />
          <DialogTitle className="text-xl">{title}</DialogTitle>
        </div>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>

      {children}

      {helperText ? (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={submitting}
          className="rounded-lg px-6"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm || submitting}
          className="gap-2 rounded-lg bg-lime-900 px-6 hover:bg-lime-950 text-white"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {submitting ? submittingLabel : confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
